import { ScreeningAppointmentType, ScreeningProgram, useScreeningQuestions, useSubmitScreening } from "../hooks"
import { SCREENING_ANSWER_LABELS, screeningQuestionText } from "../screeningCopy"
import { Alert, Box, Divider, FormLabel, ForwardIcon, Typography } from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormButton, FormRadioGroup } from "@carrotfertility/carotene-core-x"
import { ButtonSkeleton, Skeleton } from "@internal/ui"
import React from "react"
import { BookingBreadcrumbs } from "../Breadcrumbs"
import { useTrackBookingScreenView, useBookingTracking } from "../tracking"

type HealthQuestionsPageProps = {
  program: ScreeningProgram
  appointmentType: ScreeningAppointmentType
  onContinue: () => void
  onDisqualified: (exclusionMessageId: string) => void
  onReturnToProviderResults?: () => void
}

// Field values are keyed by question id -> the chosen canonical answer value (e.g. "yes").
type ScreeningFormValues = Record<string, string>

// Literal strings (no react-intl): this package is English-only — see screeningCopy.ts.
export function HealthQuestionsPage({
  program,
  appointmentType,
  onContinue,
  onDisqualified,
  onReturnToProviderResults
}: HealthQuestionsPageProps): JSX.Element {
  const questionsQuery = useScreeningQuestions(program, appointmentType)
  const submitScreening = useSubmitScreening()
  const tracking = useBookingTracking()
  useTrackBookingScreenView(
    !questionsQuery.isLoading && !questionsQuery.isError && questionsQuery.data ? { screen: "gating" } : null
  )

  const header = (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.lg) })}>
      <BookingBreadcrumbs onReturnToSearchResults={onReturnToProviderResults} />

      <Typography variant="h2" component="h1">
        A few health questions
      </Typography>
    </Box>
  )

  if (questionsQuery.isLoading) {
    return (
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.md),
          maxInlineSize: 640
        })}
      >
        {header}
        <HealthQuestionsSkeleton />
      </Box>
    )
  }

  if (questionsQuery.isError || !questionsQuery.data) {
    return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
  }

  const questions = questionsQuery.data

  // Seed every field so the radios are controlled from first render (mirrors IntakeForm).
  const defaultValues: ScreeningFormValues = Object.fromEntries(questions.map((q) => [q.id, ""]))

  const handleSubmit = (values: ScreeningFormValues) => {
    const answers = questions.map((q) => ({ questionId: q.id, answer: values[q.id] }))
    return submitScreening.mutateAsync(
      { program, appointmentType, answers },
      {
        onSuccess: (result) => {
          tracking.onEvent({
            type: "gating_result",
            result: result.eligible ? "eligible" : "ineligible",
            resultSource: "screening"
          })
          if (result.eligible) {
            onContinue()
            return
          }
          onDisqualified(result.exclusionMessageId)
        }
      }
    )
  }

  return (
    <Form<ScreeningFormValues> onSubmit={handleSubmit} defaultValues={defaultValues}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.md),
          maxInlineSize: 640
        })}
      >
        {header}

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.lg) })}
        >
          {questions.map((q, i) => (
            <React.Fragment key={q.id}>
              {/* Rule between questions (not before the first), matching the Figma. */}
              {i > 0 && <Divider />}
              <FormRadioGroup
                name={q.id}
                label={
                  <FormLabel sx={(theme) => ({ ...theme.typography.body1 })}>
                    {screeningQuestionText(q.questionTextId)}
                  </FormLabel>
                }
                required={q.required}
                controllerProps={{ rules: { required: q.required ? "Please select an answer." : false } }}
                options={(q.options ?? []).map((value) => ({ value, label: SCREENING_ANSWER_LABELS[value] ?? value }))}
              />
            </React.Fragment>
          ))}
        </Box>

        {submitScreening.isError && (
          <FormAlert severity="error">Something went wrong saving your answers. Please try again.</FormAlert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}

function HealthQuestionsSkeleton(): JSX.Element {
  return (
    <Box
      role="status"
      aria-label="Loading health questions"
      aria-busy="true"
      sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.lg) })}
    >
      {Array.from({ length: 3 }, (_, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider />}
          <Box
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
          >
            <Skeleton width="80%" height={20} />
            <Skeleton width={96} height={24} />
            <Skeleton width={96} height={24} />
          </Box>
        </React.Fragment>
      ))}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ButtonSkeleton width={144} />
      </Box>
    </Box>
  )
}
